import { useContext, useEffect } from "react";
import { CreditCard, QrCode, FileText, Inbox } from "lucide-react";
import { VendasContext } from "../context/VendasContext";
import { loadReportService, loadSalesService } from "../services";
import { formatterDate, statusVendaPago } from "../../../shared/utils";


function paymentLabel(method) {
    switch (method) {
        case "CREDIT_CARD": return "Cartão de Crédito";
        case "PIX": return "Pix";
        case "BILLET": return "Boleto";
        default: return method || "—";
    }
}

function paymentIcon(method) {
    switch (method) {
        case "CREDIT_CARD": return <CreditCard className="h-3.5 w-3.5" />;
        case "PIX": return <QrCode className="h-3.5 w-3.5" />;
        case "BILLET": return <FileText className="h-3.5 w-3.5" />;
        default: return null;
    }
}

function statusChip(status) {
    if (statusVendaPago(status)) return "chip-positive";
    if (["REEMBOLSO SOLICITADO", "REEMBOLSADO", "CANCELADO", "EXPIRADO", "REJEITADO"].includes(status)) return "chip-danger";
    if (["ATRASADO", "CHARGEBACK", "AGUARDANDO PAGAMENTO"].includes(status)) return "chip-warning";
    return "chip-neutral";
}

function platformBadge(plat) {
    const p = (plat || "").toLowerCase();
    if (p === "hotmart") return "bg-orange-500/10 text-orange-300 ring-1 ring-orange-500/20";
    if (p === "kiwify") return "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/20";
    if (p === "lastlink") return "bg-violet-500/10 text-violet-300 ring-1 ring-violet-500/20";
    return "bg-white/[0.04] text-ink-3 ring-1 ring-white/[0.06]";
}


export default function TableSales() {

    const { sales, setSales } = useContext(VendasContext);
    const { setReport } = useContext(VendasContext);
    const { search } = useContext(VendasContext);
    const { startDateFilter } = useContext(VendasContext);
    const { endDateFilter } = useContext(VendasContext);
    const { statusFilter } = useContext(VendasContext);
    const { groupProductsFilter } = useContext(VendasContext);
    const { setTotalProductsSalesCard } = useContext(VendasContext);
    const { setTotalValueCard } = useContext(VendasContext);
    const { setTotalValueProducerCard } = useContext(VendasContext);
    const { setTotalProductsRefundedCard } = useContext(VendasContext);
    const { setTotalValueRefundedCard } = useContext(VendasContext);
    const { itemsPerPage } = useContext(VendasContext);
    const { currentPage } = useContext(VendasContext);
    const { integrationFilter } = useContext(VendasContext);
    const { plataformFilter } = useContext(VendasContext);


    useEffect(() => {
        handleRefreshSales();
        handleResetCards();
        handleRefreshReport();
    }, [search, startDateFilter, endDateFilter, statusFilter, groupProductsFilter, integrationFilter, plataformFilter]);


    async function handleRefreshSales() {
        const response = await loadSalesService(statusFilter, groupProductsFilter, startDateFilter, endDateFilter, search, integrationFilter, plataformFilter);
        if (response) setSales(response.data.vendas);
    }

    function handleResetCards() {
        setTotalProductsSalesCard(0);
        setTotalValueCard(0);
        setTotalValueProducerCard(0);
        setTotalProductsRefundedCard(0);
        setTotalValueRefundedCard(0);
    }

    async function handleRefreshReport() {
        const response = await loadReportService(groupProductsFilter, startDateFilter, endDateFilter);
        if (response) setReport(response.data);
    }


    const pageSlice = sales.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


    return (
        <div className="surface-card mb-6 overflow-hidden">

            <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm" style={{ minWidth: 1080 }}>

                    <thead className="bg-surface-3/60 backdrop-blur-sm">
                        <tr className="text-left">
                            <Th>ID Venda</Th>
                            <Th>Transação</Th>
                            <Th>Plataforma</Th>
                            <Th>Data</Th>
                            <Th>Comprador</Th>
                            <Th>Produto</Th>
                            <Th align="right">Valor</Th>
                            <Th align="right">Produtor</Th>
                            <Th>Pagamento</Th>
                            <Th>Status</Th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-white/[0.04]">
                        {pageSlice.map((sale) => (
                            <tr key={sale.id} className="group hover:bg-white/[0.02] transition-colors">
                                <Td>
                                    <span className="font-mono text-xs text-ink-3">#{sale.venda_id}</span>
                                </Td>
                                <Td>
                                    <span className="font-mono text-xs text-ink-4 truncate block max-w-[140px]" title={sale.id_transacao}>
                                        {sale.id_transacao}
                                    </span>
                                </Td>
                                <Td>
                                    <span className={`chip ${platformBadge(sale.plataforma)}`}>{sale.plataforma}</span>
                                </Td>
                                <Td>
                                    <span className="text-ink-2 tabular-nums">{formatterDate(sale.data_criacao)}</span>
                                </Td>
                                <Td>
                                    <span className="text-ink-1 truncate block max-w-[180px]" title={sale.cliente_nome}>
                                        {sale.cliente_nome}
                                    </span>
                                </Td>
                                <Td>
                                    <span className="text-ink-2 truncate block max-w-[200px]" title={sale.produto_nome}>
                                        {sale.produto_nome}
                                    </span>
                                </Td>
                                <Td align="right">
                                    <span className="font-mono text-ink-1 tabular-nums">{sale.valor} <span className="text-ink-5 text-xs">{sale.moeda}</span></span>
                                </Td>
                                <Td align="right">
                                    <span className="font-mono text-positive-400 tabular-nums">{sale.valor_produtor} <span className="text-ink-5 text-xs">{sale.moeda}</span></span>
                                </Td>
                                <Td>
                                    <span className="inline-flex items-center gap-1.5 text-ink-3 text-xs">
                                        {paymentIcon(sale.metodo_pagamento)}
                                        {paymentLabel(sale.metodo_pagamento)}
                                    </span>
                                </Td>
                                <Td>
                                    <span className={statusChip(sale.status)}>{sale.status}</span>
                                </Td>
                            </tr>
                        ))}
                        {sales.length === 0 && (
                            <tr>
                                <td colSpan={10} className="p-12 text-center">
                                    <div className="flex flex-col items-center gap-2 text-ink-4">
                                        <Inbox className="h-8 w-8 text-ink-5" />
                                        <p className="text-sm">Nenhuma venda encontrada com os filtros atuais.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>

                </table>
            </div>
        </div>
    );
}


function Th({ children, align = "left" }) {
    return (
        <th className={`px-4 py-3 text-${align} text-2xs uppercase tracking-wider font-medium text-ink-4 border-b border-white/[0.06]`}>
            {children}
        </th>
    );
}

function Td({ children, align = "left" }) {
    return (
        <td className={`px-4 py-3 text-${align} align-middle`}>
            {children}
        </td>
    );
}
